export const hotelPageData = {
  stats: [
    { value: "30%", label: "Procurement Cost Savings" },
    { value: "2h+", label: "Staff Time Saved Daily" },
    { value: "99%+", label: "Supplier Compliance" },
    { value: "500+", label: "Hotels Served" },
  ],
  statsAccent: "from-orange-500 to-amber-600",

  challengeTitle: "Hotel Procurement Pain Points Are Costing Thousands Every Month",
  challengeSubtitle:
    "From food and beverage to linen and amenities — manual hospitality procurement creates cost overruns, compliance risks, and operational disruptions.",
  challenges: [
    {
      icon: "📦",
      title: "Unreliable Supplier Deliveries",
      description:
        "Inconsistent delivery timing and quality disrupts hotel operations and damages the guest experience when items are missing.",
    },
    {
      icon: "💸",
      title: "Overpaying on Every Category",
      description:
        "Without data-driven negotiation tools, hotels overpay for food, linen, amenities, and maintenance supplies consistently.",
    },
    {
      icon: "⏰",
      title: "2+ Hours of Daily Manual Work",
      description:
        "Staff spend hours on repetitive order placements, approvals, and supplier follow-ups that should be fully automated.",
    },
    {
      icon: "👁️",
      title: "Zero Consolidated Visibility",
      description:
        "No unified view of spend, inventory, or supplier performance means reactive — not proactive — procurement decisions.",
    },
    {
      icon: "⚠️",
      title: "Supply Chain Risk Exposure",
      description:
        "Inability to identify and mitigate risks leads to costly last-minute sourcing and service disruptions.",
    },
    {
      icon: "🍽️",
      title: "Food Safety Compliance Burden",
      description:
        "Staying compliant with food safety, health, and hospitality regulations is complex and resource-intensive manually.",
    },
    {
      icon: "🎯",
      title: "Quality Control Failures",
      description:
        "Inconsistent product quality from suppliers leads to guest complaints and reputation damage.",
    },
    {
      icon: "🌿",
      title: "Sustainability Reporting Gaps",
      description:
        "Guests and regulators demand sustainable sourcing, but proving it without traceability tools is nearly impossible.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-orange-50 text-orange-700 border-orange-100",
    solutionIconBg: "bg-orange-50",
    solutionIconColor: "text-orange-600",
    solutionAccent: "from-orange-500 to-amber-500",
  },

  solutionTitle: "The Smart Procurement Platform Purpose-Built for Hospitality",
  solutionSubtitle:
    "AI-powered automation, supplier intelligence, and compliance management designed for every aspect of hotel procurement.",
  solutions: [
    {
      icon: "🤖",
      title: "Automated Procurement Workflows",
      description:
        "Eliminate manual tasks with AI-driven order automation — saving your team 2+ hours of repetitive work every day.",
    },
    {
      icon: "⭐",
      title: "Supplier Performance Scoring",
      description:
        "Monitor, rank, and manage all suppliers with real-time performance data and automatic compliance alerts.",
    },
    {
      icon: "💰",
      title: "AI-Powered Cost Optimisation",
      description:
        "Spend analytics and market benchmarking identify cost-saving opportunities across every procurement category.",
    },
    {
      icon: "📊",
      title: "360° Procurement Dashboard",
      description:
        "Real-time visibility into all purchase orders, deliveries, invoices, and supplier relationships — in one live view.",
    },
    {
      icon: "🚨",
      title: "Proactive Risk Management",
      description:
        "AI monitors your hospitality supply chain 24/7 and alerts you to supply risks before they impact guest experience.",
    },
    {
      icon: "✅",
      title: "Built-In Compliance Engine",
      description:
        "Automated food safety, health, and hospitality regulation checks keep your hotel audit-ready at all times.",
    },
    {
      icon: "🎯",
      title: "Quality Assurance Tools",
      description:
        "Set quality benchmarks, track delivery compliance, and resolve issues through built-in supplier collaboration tools.",
    },
    {
      icon: "🌿",
      title: "Sustainable Sourcing Verification",
      description:
        "Trace every product to its source with supply chain transparency tools that meet ESG commitments automatically.",
    },
    {
      icon: "🔌",
      title: "PMS & ERP Integration",
      description:
        "Integrates with popular hotel Property Management Systems, ERPs, and accounting platforms for unified data flow.",
    },
  ],

  howItWorksTitle: "Hotel Procurement Transformed in 4 Steps",
  howItWorksSteps: [
    {
      icon: "📋",
      title: "Onboard Your Suppliers",
      description: "Digital supplier onboarding with automated qualification and compliance verification in minutes.",
    },
    {
      icon: "⚙️",
      title: "Configure Automation Rules",
      description: "Set reorder points, approval workflows, and compliance requirements for every category.",
    },
    {
      icon: "🤖",
      title: "AI Manages Operations",
      description: "Ordering, supplier monitoring, compliance checks, and cost analysis all run automatically.",
    },
    {
      icon: "💰",
      title: "Costs Drop, Standards Rise",
      description: "Monitor procurement savings, compliance rates, and supplier performance improving in real time.",
    },
  ],

  outcomeTitle: "The Hospitality Procurement Results That Matter",
  outcomeSubtitle: "Real outcomes from hotels and hospitality groups using EkkBaz AI.",
  outcomes: [
    {
      value: "30%",
      metric: "Procurement Cost Reduction",
      description:
        "AI spend analytics, supplier benchmarking, and automated negotiation deliver 30% average savings across all procurement categories.",
    },
    {
      value: "80%",
      metric: "Less Manual Work",
      description:
        "Procurement automation eliminates 80% of repetitive manual tasks — freeing staff to focus on guest experience.",
    },
    {
      value: "99%+",
      metric: "Supplier Compliance",
      description:
        "Automated compliance monitoring ensures all suppliers meet food safety and hospitality regulations at all times.",
    },
  ],
  outcomeImage: "/assets/images/hotel_solution.jpg",

  ctaTitle: "Join 500+ Hotels Reducing Procurement Costs by 30%",
  ctaSubtitle:
    "Get a personalised demo to see how EkkBaz AI fits your property's specific needs and delivers savings in your first quarter.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const hotelFacilities = [
  {
    id: 1,
    extraLabel: "Your Challenges",
    userType: "Procurement Pain Points Costing Hotels Thousands Every Month",
    description: {
      line1: `<strong>Unreliable Supplier Performance</strong> — Inconsistent delivery quality and timing disrupts hotel operations and damages guest experience.`,
      line2: `<strong>High Procurement Costs</strong> — Without data-driven negotiation tools, hotels overpay for food, linen, amenities, and maintenance supplies.`,
      line3: `<strong>Manual, Time-Consuming Processes</strong> — Staff spend hours daily on repetitive order placements, approvals, and follow-ups instead of serving guests.`,
      line4: `<strong>Zero Real-Time Visibility</strong> — No consolidated view of spend, inventory, or supplier performance means reactive, not proactive, decision-making.`,
    },
    banner: "/assets/images/hotel_problem.jpg",
  },
  {
    id: 2,
    extraLabel: "Our Solutions",
    userType: "EkkBaz AI — The Smart Procurement Platform for Hospitality",
    description: {
      line1: `<strong>Automated Procurement Workflows</strong> — Eliminate manual tasks with AI-driven order automation, saving your team 2+ hours every day.`,
      line2: `<strong>Supplier Performance Scoring</strong> — Monitor, rank, and manage all suppliers with real-time performance data and automatic alerts.`,
      line3: `<strong>AI-Powered Cost Optimization</strong> — Identify cost-saving opportunities across every category with spend analytics and benchmarking.`,
      line4: `<strong>360° Procurement Dashboard</strong> — Real-time visibility into all purchase orders, deliveries, invoices, and supplier relationships in one place.`,
    },
    banner: "/assets/images/hotel_solution.jpg",
  },
  {
    id: 3,
    extraLabel: "Get Started",
    userType: "Join 500+ Hotels Reducing Procurement Costs by 30%",
    description: `Hotels using EkkBaz AI save an average of 30% on procurement costs, eliminate 80% of manual procurement tasks, and improve supplier compliance to 99%+. Get a personalized demo to see how EkkBaz AI fits your property's needs.`,
    btnLabel: "Book a Free Consultation",
    formLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    banner: "/assets/images/medicine3.png",
  },
];

export const hotelFAQs = [
  {
    question: "How does EkkBaz AI help hotels reduce procurement costs?",
    answer:
      "EkkBaz AI analyses your historical spend data, benchmarks prices against market rates, and provides AI-powered recommendations for consolidating suppliers and renegotiating contracts — helping hotels reduce procurement costs by an average of 30%.",
  },
  {
    question: "Can EkkBaz AI integrate with existing hotel management systems?",
    answer:
      "Yes. EkkBaz AI is designed to integrate with popular PMS, ERP, and accounting systems used in hospitality, allowing seamless data flow between procurement, inventory, and financial reporting.",
  },
  {
    question: "How does EkkBaz AI ensure food safety compliance for hotels?",
    answer:
      "The platform continuously monitors supplier certifications and compliance documentation, automatically flags expiring certifications, and alerts procurement teams to any regulatory risks — keeping your hotel audit-ready at all times.",
  },
];
